<template>
  <form @submit.prevent="onSubmit" class="space-y-4">
    <div class="space-y-2">
      <Label :for="mode === 'create' ? 'display-name' : 'edit-display-name'">Display Name</Label>
      <Input
        :id="mode === 'create' ? 'display-name' : 'edit-display-name'"
        v-model="form.name"
        placeholder="e.g., Living Room TV"
        required
      />
    </div>

    <div class="space-y-2">
      <Label :for="mode === 'create' ? 'display-description' : 'edit-display-description'">Description</Label>
      <Input
        :id="mode === 'create' ? 'display-description' : 'edit-display-description'"
        v-model="form.description"
        placeholder="Optional description"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div class="space-y-2">
        <Label :for="mode === 'create' ? 'display-width' : 'edit-display-width'">Width (px)</Label>
        <Input
          :id="mode === 'create' ? 'display-width' : 'edit-display-width'"
          v-model.number="form.width"
          type="number"
          min="50"
          max="2000"
        />
      </div>

      <div class="space-y-2">
        <Label :for="mode === 'create' ? 'display-height' : 'edit-display-height'">Height (px)</Label>
        <Input
          :id="mode === 'create' ? 'display-height' : 'edit-display-height'"
          v-model.number="form.height"
          type="number"
          min="50"
          max="2000"
        />
      </div>
    </div>

    <!-- Position fields only for edit mode -->
    <div v-if="mode === 'edit'" class="grid grid-cols-2 gap-4">
      <div class="space-y-2">
        <Label :for="'edit-display-x'">Position X</Label>
        <Input
          id="edit-display-x"
          v-model.number="form.position_x"
          type="number"
        />
      </div>

      <div class="space-y-2">
        <Label :for="'edit-display-y'">Position Y</Label>
        <Input
          id="edit-display-y"
          v-model.number="form.position_y"
          type="number"
        />
      </div>
    </div>

    <!-- Active toggle only for edit mode -->
    <div v-if="mode === 'edit'" class="flex items-center space-x-2">
      <Switch
        id="edit-display-active"
        v-model:checked="form.is_active"
      />
      <Label for="edit-display-active">Active</Label>
    </div>

    <div class="flex justify-between pt-4">
      <!-- Delete button only for edit mode -->
      <Button
        v-if="mode === 'edit'"
        @click="handleDelete"
        variant="destructive"
        type="button"
        :disabled="isDeleting"
      >
        {{ isDeleting ? 'Deleting...' : 'Delete' }}
      </Button>

      <div class="flex space-x-2 ml-auto">
        <Button @click="$emit('cancel')" variant="outline" type="button">
          Cancel
        </Button>
        <Button type="submit" :disabled="isLoading">
          {{ isLoading ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create Display' : 'Save Changes') }}
        </Button>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useDisplaysStore } from '@/stores/displays'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useFormHandler, validators } from '@/composables/useFormHandler'
import { useStoreAction } from '@/composables/useStoreAction'
import type { Display } from '@/stores/displays'

interface Props {
  mode: 'create' | 'edit'
  display?: Display // Required for edit mode
}

const props = defineProps<Props>()

const emit = defineEmits<{
  success: []
  cancel: []
}>()

const displaysStore = useDisplaysStore()

const { form, handleSubmit } = useFormHandler({
  initialData: props.mode === 'create' ? {
    name: '',
    description: '',
    position_x: 0,
    position_y: 0,
    width: 200,
    height: 150
  } : {
    name: '',
    description: '',
    position_x: 0,
    position_y: 0,
    width: 200,
    height: 150,
    is_active: true
  },
  validateFn: validators.displayName,
  loadFrom: props.mode === 'edit' ? computed(() => props.display!) : undefined,
  mapFromSource: props.mode === 'edit' ? (source: unknown) => ({
    name: (source as Display).name,
    description: (source as Display).description || '',
    position_x: (source as Display).position_x,
    position_y: (source as Display).position_y,
    width: (source as Display).width,
    height: (source as Display).height,
    is_active: (source as Display).is_active
  }) : undefined
})

const { execute: createDisplay, isLoading: isCreating } = useStoreAction(
  () => displaysStore.createDisplay({
    name: form.value.name.trim(),
    description: form.value.description.trim() || undefined,
    position_x: form.value.position_x,
    position_y: form.value.position_y,
    width: form.value.width,
    height: form.value.height
  }),
  {
    successMessage: 'Display created successfully'
  }
)

const { execute: updateDisplay, isLoading: isUpdating } = useStoreAction(
  () => displaysStore.updateDisplayById(props.display!.id, {
    name: form.value.name.trim(),
    description: form.value.description.trim() || null,
    position_x: form.value.position_x,
    position_y: form.value.position_y,
    width: form.value.width,
    height: form.value.height,
    is_active: form.value.is_active
  }),
  {
    successMessage: 'Display updated successfully'
  }
)

const { execute: deleteDisplay, isLoading: isDeleting } = useStoreAction(
  () => displaysStore.deleteDisplay(props.display!.id),
  {
    successMessage: 'Display deleted successfully'
  }
)

const isLoading = computed(() => props.mode === 'create' ? isCreating.value : isUpdating.value)

const onSubmit = () => {
  handleSubmit(async () => {
    const success = props.mode === 'create'
      ? await createDisplay()
      : await updateDisplay()

    if (success) {
      emit('success')
    }
    return { success: !!success }
  })
}

const handleDelete = async () => {
  if (!confirm(`Are you sure you want to delete "${props.display!.name}"? This will also remove all scene configurations for this display.`)) {
    return
  }

  const success = await deleteDisplay()
  if (success) {
    emit('success')
  }
}
</script>